import React from 'react';
import { cn } from '../../lib/utils';
import { Shield, Award, Clock, Star, CheckCircle, FileCheck, AlertTriangle } from 'lucide-react';

// Common badge styles - ALL CAPS, smaller text
const badgeBaseStyles = 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider border uppercase';

export const VerificationBadge = ({ type, className }) => {
  const badges = {
    licensed: {
      icon: Shield,
      label: 'LICENSED',
      colors: 'bg-teal-500/10 text-teal-400 border-teal-500/30'
    },
    pi_licensed: {
      icon: Shield,
      label: 'PRIVATE INVESTIGATOR',
      colors: 'bg-violet-500/10 text-violet-400 border-violet-500/30'
    },
    notary: {
      icon: Shield,
      label: 'NOTARY',
      colors: 'bg-amber-500/10 text-amber-400 border-amber-500/30'
    },
    insured: {
      icon: CheckCircle,
      label: 'INSURED',
      colors: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
    },
    bonded: {
      icon: Shield,
      label: 'BONDED',
      colors: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
    }
  };

  const badge = badges[type];
  if (!badge) return null;

  const Icon = badge.icon;

  return (
    <span className={cn(badgeBaseStyles, badge.colors, className)}>
      <Icon className="h-3 w-3" />
      {badge.label}
    </span>
  );
};

export const StateCertificationBadge = ({ badge, showExpiration = false, className }) => {
  if (!badge) return null;
  
  // Check if expiring soon (< 60 days) - only if showExpiration is true
  let isExpiringSoon = false;
  let daysUntilExpiry = null;
  if (showExpiration && badge.expiration_date) {
    const expDate = new Date(badge.expiration_date + 'T23:59:59');
    const now = new Date();
    const diffTime = expDate - now;
    daysUntilExpiry = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    isExpiringSoon = daysUntilExpiry <= 60 && daysUntilExpiry > 0;
  }
  
  const daysLabel = daysUntilExpiry === 1 ? '1 DAY' : daysUntilExpiry + ' DAYS';
  
  // Color coding by certification type (amber if expiring soon)
  const colorMap = {
    certification: 'bg-violet-500/10 text-violet-400 border-violet-500/30',
    license: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30',
    registration: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    county_certification: 'bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/30'
  };
  
  const expiringColors = 'bg-amber-500/10 text-amber-400 border-amber-500/30';
  const colors = isExpiringSoon ? expiringColors : (colorMap[badge.type] || 'bg-violet-500/10 text-violet-400 border-violet-500/30');
  
  return (
    <span 
      className={cn(badgeBaseStyles, colors, className)}
      title={showExpiration && badge.expiration_date ? (isExpiringSoon ? `Expires in ${daysLabel}` : `Expires: ${badge.expiration_date}`) : undefined}
    >
      {isExpiringSoon ? <AlertTriangle className="h-3 w-3" /> : <FileCheck className="h-3 w-3" />}
      {badge.badge_text?.toUpperCase()}
      {isExpiringSoon && <span className="text-[9px] opacity-75">({daysLabel})</span>}
    </span>
  );
};

export const ExperienceBadge = ({ years, className }) => {
  if (!years) return null;
  
  return (
    <span className={cn(
      badgeBaseStyles,
      'bg-purple-500/10 text-purple-400 border-purple-500/30',
      className
    )}>
      <Award className="h-3 w-3" />
      {years}+ YRS
    </span>
  );
};

export const ResponseBadge = ({ hours, className }) => {
  if (!hours) return null;
  
  return (
    <span className={cn(
      badgeBaseStyles,
      'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
      className
    )}>
      <Clock className="h-3 w-3" />
      {hours}H RESPONSE
    </span>
  );
};

export const FoundingBadge = ({ className }) => {
  return (
    <span className={cn(
      badgeBaseStyles,
      'bg-amber-500/10 text-amber-400 border-amber-500/30',
      className
    )}>
      <Star className="h-3 w-3 fill-amber-400" />
      FOUNDING MEMBER
    </span>
  );
};

export const BadgeList = ({ 
  verifications = {}, 
  yearsExperience, 
  responseGuarantee,
  isFoundingMember,
  stateCertifications = [],
  className 
}) => {
  // Handle both array and object formats for verifications
  const verificationKeys = Array.isArray(verifications) 
    ? verifications 
    : Object.keys(verifications || {}).filter(key => 
        verifications[key]?.status === 'verified'
      );
  
  const hasContent = verificationKeys.length > 0 || yearsExperience || responseGuarantee || isFoundingMember || stateCertifications.length > 0;
  
  if (!hasContent) return null;

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {isFoundingMember && <FoundingBadge />}
      {stateCertifications.map((cert, idx) => (
        <StateCertificationBadge key={cert.state + (cert.county || '') + idx} badge={cert} />
      ))}
      {verificationKeys.map((v) => (
        <VerificationBadge key={v} type={v} />
      ))}
      {yearsExperience && <ExperienceBadge years={yearsExperience} />}
      {responseGuarantee && <ResponseBadge hours={responseGuarantee} />}
    </div>
  );
};

export default VerificationBadge;
